import React from 'react';
import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';
import { PRODUCT } from 'routes';

import { getProductList } from 'api';
import { RequestError } from 'api/utils';
import Grid from 'components/Grid';
import ProductCard from 'components/ProductCard';
import Spaced from 'components/Spaced';
import Typography from 'components/Typography';
import { Paginated } from 'types/Paginated';
import { Product } from 'types/Product';
import { chunkBy, getURLSearchParams, stringifyMultipleDesigners, toast } from 'utils';
import styles from './MoreByDesigner.module.css';

const PRODUCTS_TO_SHOW = 4;

interface MoreByDesigner {
  product: Product;
  pageSize?: number;
}

const MoreByDesigner: React.FCWithChildren<MoreByDesigner> = ({ product, pageSize = 5 }) => {
  const { designers } = product;
  const [data, setData] = React.useState<Paginated<Product>>();
  const { results: rawProducts = [], count = 0, next } = data || {};
  const [loading, setLoading] = React.useState(false);
  const initialFetch = React.useRef(false);

  // Omit product that we are currently viewing
  const products = rawProducts.filter(({ id }) => id !== product.id);

  // Get page from the next URL instead of
  // keeping track of state in the component.
  const search = getURLSearchParams(next);
  const page = Number(search.page || 1);

  const pages = Math.ceil(count / pageSize);
  const isLastPage = page === pages;

  // Since we are hiding the product we are currently viewing we might end up in a state
  // where there's only three products. Therefore we wanna make sure that we always
  // display four products by fetching an extra product and doing some chunking manually.
  const productsChunks = chunkBy(products, PRODUCTS_TO_SHOW).filter(
    arr => arr.length === PRODUCTS_TO_SHOW || isLastPage,
  );
  const productsToShow = productsChunks.slice(0, page).flat(1);

  const loadProducts = React.useCallback(
    async (p: number) => {
      try {
        setLoading(true);
        const productsResponse = await getProductList({
          designers: designers.map(({ id }) => id),
          page: p,
          page_size: pageSize,
        });

        if (productsResponse.data) {
          setData({
            ...productsResponse.data,
            results: [...products, ...productsResponse.data.results],
          });
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error instanceof RequestError) {
          Sentry.captureException(error);
          toast();
        }
      }
    },
    [designers, products, pageSize],
  );

  React.useEffect(() => {
    // Just fetch the initial products once.
    // Handle by click after this.
    if (!initialFetch.current) {
      initialFetch.current = true;
      loadProducts(page);
    }
  }, [loadProducts, page]);

  const onLoadMoreClick = () => {
    if (!isLastPage) {
      loadProducts(page + 1);
    }
  };

  return products.length > 0 ? (
    <Spaced spacing={2}>
      <Typography component="h2" variant="sub">
        {`MORE BY ${stringifyMultipleDesigners(designers)}`}
      </Typography>
      <Spaced className={styles.root} spacing={3}>
        <Grid columns={4} gap="regular">
          {productsToShow.map(product => (
            <Link
              href={{ pathname: PRODUCT.pathname, query: { id: product.slug } }}
              passHref
              key={product.id}
              legacyBehavior
            >
              <ProductCard product={product} />
            </Link>
          ))}
        </Grid>

        {!isLastPage && (
          <button disabled={loading} onClick={() => onLoadMoreClick()} className={styles.loadMore}>
            <Typography>VIEW MORE</Typography>
          </button>
        )}
      </Spaced>
    </Spaced>
  ) : null;
};

export default MoreByDesigner;
